import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomerOrderDataAuthService } from '@app/core/services/customer-order-auth/customer-order-data-auth.service';
import { CustomerOrderService } from '@app/core/services/customer-order-auth/customer-order.service';
import {
  EnquiryBasicData,
  OrderItemData,
  QuotationData,
  RelationshipManagerData,
} from '@app/core/services/customer-order/customer-order-data.service';
import { UploadAuthService } from '@app/core/services/upload/upload-auth.service';
import { UploadPublicService } from '@app/core/services/upload/upload-public.service';
import { AlertDialogComponent } from '@app/shared/components/alert-dialog/alert-dialog.component';
import { FileDownloadButtonComponent } from '@app/shared/components/file-download-button/file-download-button.component';
import { LoadingSpinnerComponent } from '@app/shared/components/loading-spinner/loading-spinner.component';
import { ReportIssueButtonComponent } from '@app/shared/components/report-issue-button/report-issue-button.component';
import { ShareRequirementsButtonComponent } from '@app/shared/components/share-requirements-button/share-requirements-button.component';
import { EntityType } from '@app/shared/enums/enums';
import { getInvoiceDeliveryStatusChipClass } from '@app/shared/functions/chips';
import { SelectionOption } from '@app/shared/interfaces/selection-option';
import { MobileNumberFormatPipe } from '@app/shared/pipes/mobile-number-format.pipe';
import { PincodeNumberFormatPipe } from '@app/shared/pipes/pincode-number-format.pipe';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-order-mobile',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatTableModule,
    MatChipsModule,
    MatPaginatorModule,
    LoadingSpinnerComponent,
    ReportIssueButtonComponent,
    FileDownloadButtonComponent,
    MobileNumberFormatPipe,
    PincodeNumberFormatPipe,
    ShareRequirementsButtonComponent,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
  ],
  providers: [CustomerOrderDataAuthService],
  templateUrl: './order-mobile.component.html',
  styleUrl: './order-mobile.component.scss',
})
export class OrderMobileComponent implements OnInit, OnDestroy {
  private routeSub!: Subscription;

  customerOrderId!: string;
  entityType!: EntityType;
  publicRequirementsId!: string;

  orderData!: { [key: string]: any };

  orderDataStruct!: { [key: string]: any };

  isLoading!: boolean;
  isScreenDesktop!: boolean;

  invoicesDataLoader: boolean = false;

  invoicesPageNumber: number = 1;
  invoicesPageSize: number = 10;
  invoicesDataLength: number = 0;

  customerName!: string;
  enquiryBasicData!: EnquiryBasicData;

  orderItems!: OrderItemData[];
  orderItemDataSource!: MatTableDataSource<OrderItemData>;

  quotationList!: QuotationData[];
  invoiceList: any = [];

  relationshipManagerData!: RelationshipManagerData;

  relationshipManagerSeniorData: RelationshipManagerData | undefined;

  requirementItemColumns: string[] = [
    'category',
    'product',
    'make',
    'grade',
    'brand',
    'type',
    'property',
    'dimension',
    'customDimensions',

    'qty',
  ];

  constructor(
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private customerOrderDataService: CustomerOrderDataAuthService,
    private customerOrderService: CustomerOrderService,
    private uploadAuthService: UploadAuthService,
    private uploadPublicService: UploadPublicService,
    private router: Router
  ) {
    this.isLoading = true;

    this.entityType = EntityType.CUSTOMER_ORDER;
  }

  async ngOnInit() {
    this.isScreenDesktop = this.route.snapshot.data['screenSize'] == 'desktop';

    // if (!this.isScreenDesktop) {
    //   this.mobileScreenDialog();
    // }

    this.routeSub = this.route.params.subscribe((params) => {
      this.customerOrderId = params['id'];
    });

    await this.customerOrderDataService.runService(this.customerOrderId);
    this.customerOrderService
      .getApiCustomerOrderInvoices(this.customerOrderId, this.invoicesPageNumber, this.invoicesPageSize)
      .subscribe({
        next: (response) => {
          this.invoiceList = response.data || [];
          this.invoicesDataLength = response.totalCount || 0;
        },
      });
    this.isLoading = false;

    this.orderData = this.customerOrderDataService.getOrderData();

    this.orderDataStruct = this.customerOrderDataService.getOrderDataStructured();

    this.customerName = this.orderDataStruct['customerName'];

    this.enquiryBasicData = this.orderDataStruct['enquiryBasicData'];

    this.relationshipManagerData = this.orderDataStruct['relationshipManagerData'];

    this.relationshipManagerSeniorData = this.orderDataStruct['relationshipManagerData']?.manager;

    this.orderItemDataSource = new MatTableDataSource(this.orderDataStruct['orderItemList']);

    this.quotationList = this.orderDataStruct['quotationList'];

    this.publicRequirementsId = this.orderDataStruct['publicRequirementsId'];
  }

  ngOnDestroy(): void {
    return;
  }

  async onChangePage(event: any) {
    if (event.pageIndex == this.invoicesPageNumber) {
      this.invoicesPageNumber = this.invoicesPageNumber + 1;
      this.invoicesDataLoader = true;
      this.customerOrderService
        .getApiCustomerOrderInvoices(this.customerOrderId, this.invoicesPageNumber, this.invoicesPageSize)
        .subscribe({
          next: (response) => {
            this.invoiceList = [...this.invoiceList, ...(response.data || [])];
            this.invoicesDataLength = response.totalCount || 0;
            this.invoicesDataLoader = false;
          },
          error: (err) => {
            this.invoicesDataLoader = false;
          },
        });
    }
  }

  getInvoiceStatusChipClass(status: SelectionOption) {
    return getInvoiceDeliveryStatusChipClass(status);
  }

  onFileDownloadAuth(fileUrl: string | null) {
    if (!fileUrl) {
      return;
    }

    this.uploadAuthService.downloadFile(fileUrl);
  }

  onFileDownloadNoAuth(fileUrl: string | null) {
    if (!fileUrl) {
      return;
    }

    this.uploadPublicService.downloadFile(fileUrl);
  }

  back(): void {
    this.router.navigate(['/customer/customer-orders']);
  }

  mobileScreenDialog() {
    this.dialog.open(AlertDialogComponent, {
      data: {
        message: 'For best experience, please visit \ncustomers.zippmat.in \non your desktop.\n',
      },
    });
  }
}
